import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  MdArchive, MdArrowBack, MdAttachFile, MdDeleteForever, MdDeleteOutline,
  MdClose, MdContacts, MdDrafts, MdEdit, MdEmail, MdForward, MdInbox, MdLockOutline,
  MdMarkEmailRead, MdMarkEmailUnread, MdOutlineMarkEmailRead, MdRefresh,
  MdPersonAdd, MdRemove, MdReply, MdReport, MdRestore, MdSave, MdSearch, MdSend, MdSettings,
  MdStar, MdStarBorder, MdVisibility,
} from 'react-icons/md';
import { useAuth } from '../../../context/AuthContext';
import MailHtmlBody from '../../../components/mail/MailHtmlBody';
import ieumLogo from '../../../assets/ieum-logo.png';
import { formatMobilePhone, isValidMobilePhone } from '../../../utils/mobilePhone';
import {
  createOwnMailbox, deleteMailContact, deleteManagedMailbox, filesToAttachments, getMailSession,
  deleteMailMessage, listMailMessages, listManagedMailboxes, readMailMessage,
  listMailContacts, readMailMessageContent, removeSpamSender, saveMailContact, saveMailDraft, sendMailMessage,
  updateMailMessages, updateMailSignature, updateManagedMailbox,
} from '../../../services/adminMailService';

const SECTION_META = {
  compose: { title: '메일발송', description: '개인 메일 또는 공용 메일 주소로 메일을 발송합니다.', icon: MdSend },
  inbox: { title: '받은편지함', description: '현재 로그인한 개인 메일로 받은 내역입니다.', icon: MdInbox },
  shared: { title: '공용메일함', description: '권한이 있는 구성원이 함께 확인하는 공용 수신함입니다.', icon: MdOutlineMarkEmailRead },
  important: { title: '중요메일함', description: '개인 중요메일과 구성원이 함께 표시한 공용 중요메일을 확인합니다.', icon: MdStar },
  sent: { title: '발신내역', description: '개인·공용 메일 주소로 발송한 내역을 확인합니다.', icon: MdEmail },
  drafts: { title: '임시저장함', description: '작성 중 저장한 메일을 이어서 편집합니다.', icon: MdDrafts },
  archive: { title: '보관함', description: '삭제하지 않고 별도로 보관한 메일입니다.', icon: MdArchive },
  spam: { title: '스팸메일함', description: '스팸으로 분류된 메일과 발신자를 확인합니다.', icon: MdReport },
  trash: { title: '휴지통', description: '삭제한 메일을 복구하거나 영구 삭제합니다.', icon: MdDeleteOutline },
  settings: { title: '메일 설정', description: '개인 메일 ID와 메일 계정을 관리합니다.', icon: MdSettings },
};

const addressText = (addresses = []) => addresses.map((item) => item.name ? `${item.name} <${item.address}>` : item.address).filter(Boolean).join(', ');
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const emailFromToken = (value = '') => {
  const text = String(value).trim();
  const bracketed = text.match(/<([^<>]+)>/);
  return String(bracketed?.[1] || text).trim().toLowerCase();
};
const parseRecipientEmails = (value = '') => [...new Set(String(value).split(/[,;\n]+/).map(emailFromToken).filter((email) => EMAIL_PATTERN.test(email)))];
const localPartLabel = (address = '') => address.split('@')[0] || address;
const notifyUnreadChanged = () => window.dispatchEvent(new Event('ieum-mail-counts-changed'));
const formatDate = (value) => value ? new Date(value).toLocaleString('ko-KR') : '-';
const formatBytes = (bytes = 0) => {
  if (!bytes) return '0 KB';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const Notice = ({ type = 'info', children }) => <NoticeBox $type={type}>{children}</NoticeBox>;

const MailSignaturePreview = ({ signature, compact = false }) => (
  <SignaturePreview $compact={compact} aria-label="메일 서명 미리보기">
    <SignatureLogo src={ieumLogo} alt="이음플랫폼" />
    <SignatureDetails>
      <strong>{signature.company}</strong>
      {signature.department && <span>{signature.department}</span>}
      <b>{signature.position} {signature.name}</b>
      <span>{signature.phone} <i> | </i> {signature.email}</span>
      <span>{signature.address}</span>
    </SignatureDetails>
  </SignaturePreview>
);

const SetupRequired = () => {
  const navigate = useNavigate();
  return (
    <EmptyCard>
      <MdSettings size={38} />
      <h3>개인 메일 ID를 먼저 설정해주세요.</h3>
      <p>설정한 ID가 <strong>아이디@ieum.in</strong> 형식의 사내 메일 주소가 됩니다.</p>
      <PrimaryButton type="button" onClick={() => navigate('/admin/mail/settings')}>메일 ID 설정</PrimaryButton>
    </EmptyCard>
  );
};

const RecipientTagsInput = ({ recipients, contacts, onChange, onOpenAddressBook }) => {
  const [draft, setDraft] = useState('');
  const [inputError, setInputError] = useState('');
  const inputRef = useRef(null);
  const selected = useMemo(() => new Set(recipients), [recipients]);
  const contactByEmail = useMemo(() => new Map(contacts.map((contact) => [contact.email, contact])), [contacts]);
  const positionGroups = useMemo(() => {
    const groups = new Map();
    contacts.filter((contact) => contact.internal && !contact.shared && contact.position).forEach((contact) => {
      if (!groups.has(contact.position)) groups.set(contact.position, []);
      groups.get(contact.position).push(contact.email);
    });
    return [...groups.entries()].sort(([left], [right]) => left.localeCompare(right, 'ko'));
  }, [contacts]);
  const suggestions = useMemo(() => {
    const keyword = draft.trim().toLowerCase();
    if (!keyword) return [];
    return contacts.filter((contact) => !selected.has(contact.email)
      && `${contact.name} ${contact.email} ${contact.department || ''} ${contact.position || ''}`.toLowerCase().includes(keyword)).slice(0, 6);
  }, [contacts, draft, selected]);

  const addEmails = (values) => {
    const nextEmails = values.map(emailFromToken).filter(Boolean);
    const invalid = nextEmails.find((email) => !EMAIL_PATTERN.test(email));
    if (invalid) {
      setInputError(`이메일 형식을 확인해 주세요: ${invalid}`);
      return false;
    }
    onChange([...new Set([...recipients, ...nextEmails])]);
    setDraft('');
    setInputError('');
    return true;
  };
  const removeEmail = (email) => onChange(recipients.filter((item) => item !== email));
  const handleKeyDown = (event) => {
    if (['Enter', ',', ';'].includes(event.key)) {
      event.preventDefault();
      if (draft.trim()) addEmails([draft]);
    } else if (event.key === 'Backspace' && !draft && recipients.length) {
      removeEmail(recipients[recipients.length - 1]);
    }
  };
  const handleChange = (event) => {
    const value = event.target.value;
    if (/[,;\n]/.test(value)) {
      const parts = value.split(/[,;\n]+/);
      const trailing = /[,;\n]$/.test(value) ? '' : parts.pop();
      const complete = parts.filter((item) => item.trim());
      if (complete.length) addEmails(complete);
      setDraft(trailing || '');
      return;
    }
    setDraft(value);
    setInputError('');
  };

  return (
    <RecipientInputWrap>
      <RecipientInputBox onClick={() => inputRef.current?.focus()} $error={Boolean(inputError)}>
        {recipients.map((email) => {
          const contact = contactByEmail.get(email);
          const label = contact?.name ? `${contact.name}${contact.position ? ` ${contact.position}` : ''} · ${email}` : email;
          return <RecipientTag key={email}><span>{label}</span><button type="button" aria-label={`${email} 제거`} onClick={(event) => { event.stopPropagation(); removeEmail(email); }}><MdRemove size={15} /></button></RecipientTag>;
        })}
        <input
          ref={inputRef}
          id="mail-to"
          aria-label="받는 사람"
          value={draft}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={() => { if (draft.trim() && EMAIL_PATTERN.test(emailFromToken(draft))) addEmails([draft]); }}
          placeholder={recipients.length ? '이메일 추가 입력' : '이메일 입력 후 Enter'}
          autoComplete="off"
        />
        <RecipientPositionSelect
          aria-label="직책별 사내 수신자 추가"
          disabled={!positionGroups.length}
          value=""
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => { if (event.target.value) addEmails(positionGroups.find(([position]) => position === event.target.value)?.[1] || []); }}
        >
          <option value="">직책 선택</option>
          {positionGroups.map(([position, emails]) => <option key={position} value={position}>{position} · {emails.length}명</option>)}
        </RecipientPositionSelect>
        <AddressBookButton type="button" onClick={(event) => { event.stopPropagation(); onOpenAddressBook(); }}><MdContacts size={17} /> 주소록 목록</AddressBookButton>
      </RecipientInputBox>
      {inputError && <RecipientInputError>{inputError}</RecipientInputError>}
      {suggestions.length > 0 && <ContactSuggestions role="listbox" aria-label="수신자 자동검색 결과">
        {suggestions.map((contact) => <button type="button" role="option" aria-selected="false" key={contact.email} onMouseDown={(event) => event.preventDefault()} onClick={() => addEmails([contact.email])}><strong>{contact.name}</strong><span>{contact.position ? `${contact.position} · ` : ''}{contact.email}</span></button>)}
      </ContactSuggestions>}
    </RecipientInputWrap>
  );
};

const AddressBookPicker = ({ contacts, error, onApply, onDelete, onClose }) => {
  const [query, setQuery] = useState('');
  const [checked, setChecked] = useState([]);
  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return contacts;
    return contacts.filter((contact) => `${contact.name} ${contact.email} ${contact.department || ''} ${contact.position || ''}`.toLowerCase().includes(keyword));
  }, [contacts, query]);
  const allChecked = filtered.length > 0 && filtered.every((contact) => checked.includes(contact.email));
  return (
    <AddressBookModal role="dialog" aria-modal="true" aria-labelledby="address-book-title" onClick={(event) => event.stopPropagation()}>
      <SignatureModalHeader>
        <div><CardTitle id="address-book-title">주소록 목록</CardTitle><CardDescription>검색 후 여러 주소를 체크해 받는 사람에 한 번에 추가할 수 있습니다.</CardDescription></div>
        <IconButton type="button" onClick={onClose} aria-label="주소록 닫기"><MdClose size={20} /></IconButton>
      </SignatureModalHeader>
      {error && <Notice type="error">{error}</Notice>}
      <AddressBookSearch><MdSearch /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="이름 또는 이메일 검색" autoFocus /></AddressBookSearch>
      <AddressBookSelectAll>
        <input type="checkbox" checked={allChecked} onChange={(event) => setChecked(event.target.checked ? filtered.map((contact) => contact.email) : checked.filter((email) => !filtered.some((contact) => contact.email === email)))} />
        <span>검색 결과 전체 선택</span><b>{filtered.length}개</b>
      </AddressBookSelectAll>
      <AddressBookList>
        {filtered.length === 0 ? <AddressBookEmpty>조건에 맞는 주소가 없습니다.</AddressBookEmpty> : filtered.map((contact) => (
          <AddressBookRow key={contact.email}>
            <label><input type="checkbox" checked={checked.includes(contact.email)} onChange={(event) => setChecked((previous) => event.target.checked ? [...new Set([...previous, contact.email])] : previous.filter((email) => email !== contact.email))} /><span><strong>{contact.name}{contact.position ? ` · ${contact.position}` : ''}</strong><small>{contact.email}{contact.internal ? ' · 사내' : ''}</small></span></label>
            {contact.saved ? <SmallDanger type="button" onClick={() => onDelete(contact.email)}>삭제</SmallDanger> : <InternalContactMark>사내</InternalContactMark>}
          </AddressBookRow>
        ))}
      </AddressBookList>
      <SignatureActions><SecondaryButton type="button" onClick={onClose}>취소</SecondaryButton><PrimaryButton type="button" disabled={!checked.length} onClick={() => onApply(checked)}>{checked.length}개 주소 추가</PrimaryButton></SignatureActions>
    </AddressBookModal>
  );
};

const ComposeView = ({ session, seed, reloadSession }) => {
  const initial = useMemo(() => ({
    from: seed?.from || session.mailAddress,
    to: seed?.to || '', cc: seed?.cc || '', bcc: seed?.bcc || '',
    subject: seed?.subject || '', text: seed?.text || '',
  }), [session.mailAddress, seed]);
  const [form, setForm] = useState(initial);
  const [files, setFiles] = useState([]);
  const [savedAttachments, setSavedAttachments] = useState(seed?.attachments || []);
  const [draftUid, setDraftUid] = useState(seed?.draftUid || '');
  const [status, setStatus] = useState(null);
  const [working, setWorking] = useState('');
  const [signatureSettingsOpen, setSignatureSettingsOpen] = useState(false);
  const [toRecipients, setToRecipients] = useState(() => parseRecipientEmails(seed?.to || ''));
  const [savedContacts, setSavedContacts] = useState([]);
  const [internalContacts, setInternalContacts] = useState([]);
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [contactError, setContactError] = useState('');
  const contacts = useMemo(() => {
    const merged = new Map((internalContacts || []).map((contact) => [contact.email, { ...contact, saved: false }]));
    (savedContacts || []).forEach((contact) => {
      const internal = merged.get(contact.email);
      merged.set(contact.email, { ...internal, ...contact, internal: Boolean(internal), shared: Boolean(internal?.shared), saved: true });
    });
    return [...merged.values()].sort((left, right) => `${left.name}\u0000${left.email}`.localeCompare(`${right.name}\u0000${right.email}`, 'ko'));
  }, [internalContacts, savedContacts]);

  useEffect(() => {
    setForm(initial);
    setSavedAttachments(seed?.attachments || []);
    setDraftUid(seed?.draftUid || '');
    setToRecipients(parseRecipientEmails(seed?.to || ''));
  }, [initial, seed]);
  const loadContacts = useCallback(async () => {
    setContactError('');
    try {
      const result = await listMailContacts();
      setSavedContacts(result.contacts || []);
      setInternalContacts(result.internalContacts || []);
    } catch (error) {
      setContactError(error.message);
      setSavedContacts([]);
      setInternalContacts([]);
    }
  }, []);
  useEffect(() => { void loadContacts(); }, [loadContacts]);
  const update = (event) => setForm((previous) => ({ ...previous, [event.target.name]: event.target.value }));

  const collectAttachments = async () => [...savedAttachments, ...(await filesToAttachments(files))];

  const submit = async (event) => {
    event.preventDefault();
    setStatus(null);
    if (!toRecipients.length) {
      setStatus({ type: 'error', text: '받는 사람 이메일을 입력한 뒤 Enter를 눌러 추가해 주세요.' });
      return;
    }
    setWorking('send');
    try {
      const attachments = await collectAttachments();
      const result = await sendMailMessage({ ...form, to: toRecipients.join(', '), attachments, draftUid });
      setStatus({ type: 'success', text: `메일을 발송했습니다.${result.rejected?.length ? ` 거부: ${result.rejected.join(', ')}` : ''}` });
      setForm((previous) => ({ from: previous.from, to: '', cc: '', bcc: '', subject: '', text: '' }));
      setFiles([]);
      setSavedAttachments([]);
      setDraftUid('');
      setToRecipients([]);
    } catch (error) {
      setStatus({ type: 'error', text: error.message });
    } finally {
      setWorking('');
    }
  };

  const saveDraft = async () => {
    setStatus(null);
    setWorking('save');
    try {
      const attachments = await collectAttachments();
      const result = await saveMailDraft({ ...form, to: toRecipients.join(', '), attachments, previousUid: draftUid });
      setDraftUid(result.uid || draftUid);
      setSavedAttachments(attachments);
      setFiles([]);
      setStatus({ type: 'success', text: `임시저장했습니다. ${formatDate(result.savedAt)}` });
    } catch (error) {
      setStatus({ type: 'error', text: error.message });
    } finally { setWorking(''); }
  };

  return (
    <>
    <ComposeCard as="form" onSubmit={submit}>
      {status && <Notice type={status.type}>{status.text}</Notice>}
      <FormRow>
        <FieldLabel htmlFor="mail-from">보내는 사람</FieldLabel>
        <Select id="mail-from" name="from" value={form.from} onChange={update} required>
          <option value={session.mailAddress}>{session.mailAddress} (개인)</option>
          {session.sharedAddresses.map((address) => <option key={address} value={address}>{address} (공용)</option>)}
        </Select>
      </FormRow>
      <FormRow>
        <FieldLabel htmlFor="mail-to">받는 사람</FieldLabel>
        <RecipientTagsInput recipients={toRecipients} contacts={contacts} onChange={setToRecipients} onOpenAddressBook={() => setContactPickerOpen(true)} />
      </FormRow>
      <FormRow>
        <FieldLabel htmlFor="mail-cc">참조</FieldLabel>
        <Input id="mail-cc" name="cc" value={form.cc} onChange={update} placeholder="선택 입력" />
      </FormRow>
      <FormRow>
        <FieldLabel htmlFor="mail-bcc">숨은 참조</FieldLabel>
        <Input id="mail-bcc" name="bcc" value={form.bcc} onChange={update} placeholder="선택 입력" />
      </FormRow>
      <FormRow>
        <FieldLabel htmlFor="mail-subject">제목</FieldLabel>
        <Input id="mail-subject" name="subject" value={form.subject} onChange={update} placeholder="메일 제목" required />
      </FormRow>
      <MessageInput name="text" value={form.text} onChange={update} placeholder="메일 내용을 입력하세요." required />
      <ComposeSignatureArea>
        <ComposeSignatureHeader>
          <span>{session.signature?.enabled ? '발송 시 자동 첨부되는 서명' : '현재 자동 서명을 사용하지 않습니다.'}</span>
          <SmallButton type="button" onClick={() => setSignatureSettingsOpen(true)}><MdSettings size={15} /> 서명 설정</SmallButton>
        </ComposeSignatureHeader>
        {session.signature?.enabled
          ? <MailSignaturePreview signature={session.signature} compact />
          : <Muted>서명 설정에서 사용 여부와 표시 정보를 지정할 수 있습니다.</Muted>}
      </ComposeSignatureArea>
      <ComposeFooter>
        <FileLabel>
          <MdAttachFile size={18} /> 첨부파일
          <input type="file" multiple onChange={(event) => setFiles(Array.from(event.target.files || []))} />
        </FileLabel>
        <FileSummary>{files.length || savedAttachments.length ? `${files.length + savedAttachments.length}개 첨부 · 전체 10MB 이하` : '전체 10MB 이하'}</FileSummary>
        <SecondaryButton type="button" disabled={Boolean(working)} onClick={saveDraft}>
          <MdSave size={17} /> {working === 'save' ? '저장 중...' : '임시저장'}
        </SecondaryButton>
        <PrimaryButton type="submit" disabled={Boolean(working)}>
          <MdSend size={17} /> {working === 'send' ? '발송 중...' : '메일 발송'}
        </PrimaryButton>
      </ComposeFooter>
    </ComposeCard>
    {signatureSettingsOpen && (
      <ModalOverlay role="presentation" onClick={() => setSignatureSettingsOpen(false)}>
        <SignatureSettings
          session={session}
          reloadSession={reloadSession}
          modal
          onClose={() => setSignatureSettingsOpen(false)}
          onSaved={() => setSignatureSettingsOpen(false)}
        />
      </ModalOverlay>
    )}
    {contactPickerOpen && <ModalOverlay role="presentation" onClick={() => setContactPickerOpen(false)}>
      <AddressBookPicker
        contacts={contacts}
        error={contactError}
        onClose={() => setContactPickerOpen(false)}
        onApply={(emails) => { setToRecipients((previous) => [...new Set([...previous, ...emails])]); setContactPickerOpen(false); }}
        onDelete={async (email) => {
          setContactError('');
          try { await deleteMailContact(email); setSavedContacts((previous) => previous.filter((contact) => contact.email !== email)); }
          catch (error) { setContactError(error.message); }
        }}
      />
    </ModalOverlay>}
    </>
  );
};

const MailboxView = ({ session, section }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const actingAs = section === 'shared' ? '' : searchParams.get('as') || '';
  const [sharedAddress, setSharedAddress] = useState('all');
  const [folderAccount, setFolderAccount] = useState(section === 'important' ? 'all-mail' : 'personal');
  const [messages, setMessages] = useState([]);
  const [counts, setCounts] = useState({ total: 0, unread: 0 });
  const [selected, setSelected] = useState(null);
  const [checked, setChecked] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [contactStatus, setContactStatus] = useState(null);
  const kind = ['sent', 'drafts', 'archive', 'spam', 'trash'].includes(section) ? section : 'inbox';
  const supportsAccountChoice = ['important', 'sent', 'archive', 'spam', 'trash'].includes(section);
  const selectedAccount = section === 'shared' ? sharedAddress : (supportsAccountChoice ? folderAccount : 'personal');
  const scope = selectedAccount === 'all-mail' ? 'all' : (selectedAccount === 'personal' ? 'personal' : 'shared');
  const address = scope === 'shared' ? (selectedAccount === 'shared-all' ? 'all' : selectedAccount) : '';

  useEffect(() => {
    setFolderAccount(section === 'important' ? 'all-mail' : 'personal');
  }, [section]);

  const query = useMemo(() => ({ scope, kind, address, as: actingAs, limit: 100, flagged: section === 'important' ? 1 : '' }), [scope, kind, address, actingAs, section]);
  const queryForMessage = useCallback((message) => ({
    ...query,
    ...(message?.mailboxScope === 'personal' ? { scope: 'personal', address: '' } : {}),
    ...(message?.mailboxAddress ? { scope: 'shared', address: message.mailboxAddress } : {}),
  }), [query]);
  const messageKey = (message) => `${message.mailboxScope === 'personal' ? 'personal' : (message.mailboxAddress || address || 'personal')}:${message.id}`;

  const load = useCallback(async () => {
    setLoading(true); setError(''); setSelected(null); setChecked([]);
    try {
      const result = await listMailMessages(query);
      setMessages(result.messages || []);
      setCounts({ total: result.total || 0, unread: result.unread || 0 });
    } catch (requestError) {
      setError(requestError.message); setMessages([]); setCounts({ total: 0, unread: 0 });
    } finally { setLoading(false); }
  }, [query]);
  useEffect(() => { void load(); }, [load]);

  const visibleMessages = useMemo(() => messages.filter((message) => {
    if (section === 'important' && !message.flagged) return false;
    if (filter === 'unread' && message.seen) return false;
    if (filter === 'read' && !message.seen) return false;
    if (filter === 'starred' && !message.flagged) return false;
    const keyword = search.trim().toLowerCase();
    if (!keyword) return true;
    return `${addressText(message.from)} ${addressText(message.to)} ${message.subject}`.toLowerCase().includes(keyword);
  }), [messages, filter, search, section]);

  const openMessage = async (message) => {
    setError('');
    setContactStatus(null);
    try {
      setSelected({ ...message, loading: true });
      const detail = await readMailMessage(message.id, queryForMessage(message));
      setSelected({ ...detail, mailboxAddress: message.mailboxAddress, mailboxScope: message.mailboxScope, flagged: message.flagged });
      setMessages((previous) => previous.map((item) => messageKey(item) === messageKey(message) ? { ...item, seen: true } : item));
      if (!message.seen) setCounts((previous) => ({ ...previous, unread: Math.max(0, previous.unread - 1) }));
      notifyUnreadChanged();
    } catch (requestError) { setSelected(null); setError(requestError.message); }
  };

  const saveSenderToContacts = async () => {
    const sender = selected?.from?.[0];
    if (!sender?.address) return;
    setContactStatus({ type: 'loading', text: '주소록에 저장 중...' });
    try {
      const result = await saveMailContact({ name: sender.name || '', email: sender.address });
      setContactStatus({ type: 'success', text: result.created ? '발신자를 주소록에 저장했습니다.' : '주소록 정보를 최신 상태로 갱신했습니다.' });
    } catch (requestError) {
      setContactStatus({ type: 'error', text: requestError.message });
    }
  };

  const runAction = async (action, targets, sender = '') => {
    const selectedTargets = targets?.length ? targets : messages.filter((message) => checked.includes(messageKey(message)));
    if (!selectedTargets.length) return;
    const isImportanceAction = action === 'star' || action === 'unstar';
    const nextFlagged = action === 'star';
    const changedImportance = isImportanceAction
      ? selectedTargets.filter((message) => Boolean(message.flagged) !== nextFlagged)
      : [];
    const importanceDelta = nextFlagged ? changedImportance.length : -changedImportance.length;
    const importanceUnreadDelta = nextFlagged
      ? changedImportance.filter((message) => !message.seen).length
      : -changedImportance.filter((message) => !message.seen).length;
    const targetKeys = new Set(selectedTargets.map(messageKey));
    setError('');
    if (isImportanceAction) {
      setMessages((previous) => previous.map((message) => targetKeys.has(messageKey(message)) ? { ...message, flagged: nextFlagged } : message));
      setSelected((previous) => previous && targetKeys.has(messageKey(previous)) ? { ...previous, flagged: nextFlagged } : previous);
      if (section === 'important') {
        setCounts((previous) => ({
          total: Math.max(0, previous.total + importanceDelta),
          unread: Math.max(0, previous.unread + importanceUnreadDelta),
        }));
      }
    }
    try {
      const groups = selectedTargets.reduce((map, message) => {
        const actionQuery = queryForMessage(message);
        const key = `${actionQuery.scope}:${actionQuery.address || ''}`;
        if (!map[key]) map[key] = { actionQuery, messages: [] };
        map[key].messages.push(message);
        return map;
      }, {});
      for (const group of Object.values(groups)) {
        await updateMailMessages(group.actionQuery, action, group.messages.map((message) => message.id), sender);
      }
      if (!isImportanceAction) {
        setSelected(null);
        await load();
      }
      notifyUnreadChanged();
    } catch (requestError) {
      if (isImportanceAction) {
        setMessages((previous) => previous.map((message) => targetKeys.has(messageKey(message)) ? { ...message, flagged: !nextFlagged } : message));
        setSelected((previous) => previous && targetKeys.has(messageKey(previous)) ? { ...previous, flagged: !nextFlagged } : previous);
        if (section === 'important') {
          setCounts((previous) => ({
            total: Math.max(0, previous.total - importanceDelta),
            unread: Math.max(0, previous.unread - importanceUnreadDelta),
          }));
        }
      }
      setError(requestError.message);
    }
  };

  const replyToMessage = () => {
    const sender = selected.from?.[0]?.address || '';
    const quoted = `\n\n----- 원본 메일 -----\n보낸 사람: ${addressText(selected.from)}\n보낸 날짜: ${formatDate(selected.date)}\n제목: ${selected.subject}\n\n${selected.text || ''}`;
    navigate('/admin/mail/compose', { state: { mailDraft: {
      from: selected.mailboxAddress || session.mailAddress,
      to: sender, subject: /^re:/i.test(selected.subject) ? selected.subject : `Re: ${selected.subject}`, text: quoted,
    } } });
  };

  const forwardMessage = async (editDraft = false) => {
    setError('');
    try {
      const attachmentResult = await readMailMessageContent(selected.id, queryForMessage(selected));
      const content = { ...selected, attachments: attachmentResult.attachments || [] };
      const forwardedText = editDraft ? content.text : `\n\n----- 전달 메일 -----\n보낸 사람: ${addressText(content.from)}\n보낸 날짜: ${formatDate(content.date)}\n받는 사람: ${addressText(content.to)}\n제목: ${content.subject}\n\n${content.text || ''}`;
      navigate('/admin/mail/compose', { state: { mailDraft: {
        from: editDraft ? (content.from?.[0]?.address || session.mailAddress) : session.mailAddress,
        to: editDraft ? addressText(content.to) : '', cc: editDraft ? addressText(content.cc) : '',
        subject: editDraft ? content.subject : (/^fwd:/i.test(content.subject) ? content.subject : `Fwd: ${content.subject}`),
        text: forwardedText, attachments: content.attachments || [], draftUid: editDraft ? selected.id : '',
      } } });
    } catch (requestError) { setError(requestError.message); }
  };

  const permanentlyDelete = async () => {
    if (!window.confirm('선택한 메일을 복구할 수 없도록 영구 삭제하시겠습니까?')) return;
    try { await deleteMailMessage(selected.id, queryForMessage(selected)); setSelected(null); await load(); }
    catch (requestError) { setError(requestError.message); }
  };

  if (selected) {
    const sender = selected.from?.[0]?.address || '';
    return (
      <MessageDetail>
        <DetailToolbar>
          <SecondaryButton type="button" onClick={() => setSelected(null)}><MdArrowBack size={18} /> 목록</SecondaryButton>
          {!selected.loading && <DetailActions>
            {section === 'drafts' ? <SecondaryButton type="button" onClick={() => forwardMessage(true)}><MdEdit /> 이어쓰기</SecondaryButton> : <>
              <SecondaryButton type="button" onClick={replyToMessage}><MdReply /> 답장</SecondaryButton>
              <SecondaryButton type="button" onClick={() => forwardMessage(false)}><MdForward /> 전달</SecondaryButton>
            </>}
            <IconButton type="button" title={selected.flagged ? '중요 해제' : '중요 표시'} onClick={() => runAction(selected.flagged ? 'unstar' : 'star', [selected])}>{selected.flagged ? <MdStar /> : <MdStarBorder />}</IconButton>
            <SecondaryButton type="button" onClick={() => runAction('unread', [selected])}><MdMarkEmailUnread /> 안읽음</SecondaryButton>
            {!['archive', 'drafts', 'trash'].includes(section) && <SecondaryButton type="button" onClick={() => runAction('archive', [selected])}><MdArchive /> 보관</SecondaryButton>}
            {!['spam', 'drafts', 'trash'].includes(section) && sender && <SecondaryButton type="button" onClick={() => runAction('spam-register', [selected], sender)}><MdReport /> 스팸등록</SecondaryButton>}
            {['archive', 'spam', 'trash'].includes(section) && <SecondaryButton type="button" onClick={() => runAction('restore', [selected])}><MdRestore /> 받은편지함 복구</SecondaryButton>}
            {section !== 'trash' && <SecondaryButton type="button" onClick={() => runAction('trash', [selected])}><MdDeleteOutline /> 삭제</SecondaryButton>}
            {['trash', 'spam', 'drafts'].includes(section) && <DangerButton type="button" onClick={permanentlyDelete}><MdDeleteForever /> 영구삭제</DangerButton>}
          </DetailActions>}
          {selected.loading && <Muted>메일을 불러오는 중...</Muted>}
        </DetailToolbar>
        {error && <Notice type="error">{error}</Notice>}
        {!selected.loading && <>
          <DetailSubject>{selected.subject}</DetailSubject>
          {selected.mailboxAddress && <DetailMeta><strong>수신처</strong><span>{localPartLabel(selected.mailboxAddress)}</span></DetailMeta>}
          <DetailMeta><strong>보낸 사람</strong><ContactMetaValue><span>{addressText(selected.from)}</span>{selected.from?.[0]?.address && emailFromToken(selected.from[0].address) !== emailFromToken(session.mailAddress) && <SmallButton type="button" disabled={contactStatus?.type === 'loading'} onClick={saveSenderToContacts}><MdPersonAdd size={15} /> {contactStatus?.type === 'loading' ? '저장 중' : '주소록 저장'}</SmallButton>}</ContactMetaValue></DetailMeta>
          <DetailMeta><strong>받는 사람</strong><span>{addressText(selected.to)}</span></DetailMeta>
          {selected.cc?.length > 0 && <DetailMeta><strong>참조</strong><span>{addressText(selected.cc)}</span></DetailMeta>}
          <DetailMeta><strong>보낸 시각</strong><span>{formatDate(selected.date)}</span></DetailMeta>
          {contactStatus && contactStatus.type !== 'loading' && <InlineContactNotice $error={contactStatus.type === 'error'}>{contactStatus.text}</InlineContactNotice>}
          {selected.attachments?.length > 0 && <AttachmentList>{selected.attachments.map((item) => <span key={`${item.filename}-${item.size}`}><MdAttachFile /> {item.filename} · {formatBytes(item.size)}</span>)}</AttachmentList>}
          {selected.html
            ? <MailHtmlBody html={selected.html} title={`${selected.subject || '제목 없음'} HTML 메일 본문`} />
            : <MessageBody>{selected.text || '본문이 없는 메일입니다.'}</MessageBody>}
        </>}
      </MessageDetail>
    );
  }

  const allVisibleChecked = visibleMessages.length > 0 && visibleMessages.every((message) => checked.includes(messageKey(message)));
  return (
    <MailboxCard>
      <MailboxToolbar>
        <ToolbarGroup>
          {section === 'shared' && <Select aria-label="공용 메일 주소" value={sharedAddress} onChange={(event) => setSharedAddress(event.target.value)}>
            <option value="all">전체보기 (desk · biz · no-reply)</option>
            {session.sharedAddresses.map((item) => <option key={item} value={item}>{localPartLabel(item)}</option>)}
          </Select>}
          {supportsAccountChoice && <Select aria-label="메일 계정" value={folderAccount} onChange={(event) => setFolderAccount(event.target.value)}>
            {section === 'important' && <option value="all-mail">전체 중요메일 (개인 · 공용)</option>}
            <option value="personal">{actingAs || session.mailAddress} (개인)</option>
            <option value={section === 'important' ? 'shared-all' : 'all'}>공용 전체보기</option>
            {session.sharedAddresses.map((item) => <option key={item} value={item}>{localPartLabel(item)} (공용)</option>)}
          </Select>}
          <CountBadge>전체 {counts.total} · 안읽음 {counts.unread}</CountBadge>
          {actingAs && <AdminReadBadge>관리자 열람: {actingAs}</AdminReadBadge>}
        </ToolbarGroup>
        <ToolbarActions>
          <SecondaryButton type="button" onClick={() => navigate('/admin/mail/compose')}><MdSend /> 발신하기</SecondaryButton>
          <SecondaryButton type="button" onClick={() => setFilter(filter === 'unread' ? 'all' : 'unread')}><MdMarkEmailUnread /> 미확인메일</SecondaryButton>
          {actingAs && <SecondaryButton type="button" onClick={() => setSearchParams({})}>내 메일로</SecondaryButton>}
          <IconButton type="button" aria-label="새로고침" onClick={load}><MdRefresh size={19} /></IconButton>
        </ToolbarActions>
      </MailboxToolbar>
      <FilterToolbar>
        <SearchBox><MdSearch /><input aria-label="메일 검색" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="보낸 사람·받는 사람·제목 검색" /></SearchBox>
        <FilterSelect aria-label="메일 상태 필터" value={filter} onChange={(event) => setFilter(event.target.value)}><option value="all">전체 메일</option><option value="unread">안읽은 메일</option><option value="read">읽은 메일</option><option value="starred">중요 메일</option></FilterSelect>
      </FilterToolbar>
      {checked.length > 0 && <BulkToolbar>
        <strong>{checked.length}개 선택</strong>
        <SmallButton type="button" onClick={() => runAction('read')}><MdMarkEmailRead /> 읽음</SmallButton>
        <SmallButton type="button" onClick={() => runAction('unread')}><MdMarkEmailUnread /> 안읽음</SmallButton>
        {!['archive', 'drafts', 'trash'].includes(section) && <SmallButton type="button" onClick={() => runAction('archive')}><MdArchive /> 보관</SmallButton>}
        {['archive', 'spam', 'trash'].includes(section) && <SmallButton type="button" onClick={() => runAction('restore')}><MdRestore /> 복구</SmallButton>}
        {section !== 'trash' && <SmallDanger type="button" onClick={() => runAction('trash')}><MdDeleteOutline /> 삭제</SmallDanger>}
        {section === 'trash' && <SmallDanger type="button" onClick={() => runAction('delete-permanently')}><MdDeleteForever /> 영구삭제</SmallDanger>}
      </BulkToolbar>}
      {error && <Notice type="error">{error}</Notice>}
      {loading ? <ListState>메일 목록을 불러오는 중...</ListState> : visibleMessages.length === 0 ? <ListState>조건에 맞는 메일이 없습니다.</ListState> : <MessageTableWrap>
        <MessageTable><thead><tr>
          <th><input aria-label="현재 목록 전체 선택" type="checkbox" checked={allVisibleChecked} onChange={(event) => setChecked(event.target.checked ? visibleMessages.map(messageKey) : [])} /></th>
          <th>중요</th>{(scope === 'all' || address === 'all') && <th>수신처</th>}<th>보낸 사람</th><th>제목</th><th>날짜</th><th>크기</th>
        </tr></thead><tbody>{visibleMessages.map((message) => {
          const key = messageKey(message);
          return <MessageRow key={key} data-mail-row $unread={!message.seen} onClick={() => openMessage(message)}>
            <td data-cell="select" onClick={(event) => event.stopPropagation()}><input aria-label={`${message.subject} 선택`} type="checkbox" checked={checked.includes(key)} onChange={(event) => setChecked((previous) => event.target.checked ? [...previous, key] : previous.filter((item) => item !== key))} /></td>
            <td data-cell="star" onClick={(event) => event.stopPropagation()}><StarButton type="button" aria-label={message.flagged ? '중요 해제' : '중요 표시'} onClick={() => runAction(message.flagged ? 'unstar' : 'star', [message])}>{message.flagged ? <MdStar /> : <MdStarBorder />}</StarButton></td>
            {(scope === 'all' || address === 'all') && <td data-cell="recipient"><RecipientBadge>{message.mailboxScope === 'personal' ? '개인' : localPartLabel(message.mailboxAddress)}</RecipientBadge></td>}
            <td data-cell="sender">{addressText(message.from) || '-'}</td>
            <td data-cell="subject">{message.subject}</td>
            <td data-cell="date" data-size={formatBytes(message.size)}>{formatDate(message.date)}</td>
            <td data-cell="size">{formatBytes(message.size)}</td>
          </MessageRow>;
        })}</tbody></MessageTable>
      </MessageTableWrap>}
    </MailboxCard>
  );
};

const MailIdCard = ({ session, onCreated }) => {
  const [mailId, setMailId] = useState(session.suggestedId || '');
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const create = async (event) => {
    event.preventDefault();
    setStatus(null);
    setSubmitting(true);
    try {
      await createOwnMailbox(mailId);
      setStatus({ type: 'success', text: '개인 메일이 생성되었습니다.' });
      await onCreated();
    } catch (error) {
      setStatus({ type: 'error', text: error.message });
    } finally { setSubmitting(false); }
  };

  return (
    <SettingsCard>
      <CardTitle>내 개인 메일</CardTitle>
      {session.configured ? (
        <ConfiguredBox><MdLockOutline size={20} /><div><strong>{session.mailAddress}</strong><span>현재 로그인 ID에 연결된 개인 메일입니다.</span></div></ConfiguredBox>
      ) : (
        <form onSubmit={create}>
          <CardDescription>영문 소문자, 숫자, 점(.), 밑줄(_), 하이픈(-)으로 3~32자까지 설정할 수 있습니다. 생성 후에는 관리자만 수정할 수 있습니다.</CardDescription>
          {status && <Notice type={status.type}>{status.text}</Notice>}
          <MailIdLine>
            <Input aria-label="개인 메일 ID" value={mailId} onChange={(event) => setMailId(event.target.value.toLowerCase())} placeholder="mail-id" pattern="[a-z0-9][a-z0-9._-]{1,30}[a-z0-9]" required />
            <DomainSuffix>@ieum.in</DomainSuffix>
            <PrimaryButton type="submit" disabled={submitting}>{submitting ? '중복 확인 중...' : '메일 생성'}</PrimaryButton>
          </MailIdLine>
        </form>
      )}
    </SettingsCard>
  );
};

const SignatureSettings = ({ session, reloadSession, modal = false, onClose, onSaved }) => {
  const [form, setForm] = useState(session.signature || null);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(session.signature || null); }, [session.signature]);
  if (!session.configured || !form) return null;

  const updateField = (event) => {
    const { name, value, checked, type } = event.target;
    const nextValue = name === 'phone' ? formatMobilePhone(value) : value;
    setForm((previous) => ({ ...previous, [name]: type === 'checkbox' ? checked : nextValue }));
  };
  const save = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setStatus(null);
    if (form.phone && !isValidMobilePhone(form.phone)) {
      setStatus({ type: 'error', text: '연락처는 010으로 시작하는 11자리를 010-0000-0000 형식으로 입력해 주세요.' });
      return;
    }
    setSaving(true);
    try {
      const result = await updateMailSignature({
        enabled: form.enabled,
        department: form.department,
        position: form.position,
        name: form.name,
        phone: form.phone,
      });
      setForm(result.signature);
      setStatus({ type: 'success', text: '서명 설정을 저장했습니다.' });
      onSaved?.(result.signature);
      await reloadSession();
    } catch (error) {
      setStatus({ type: 'error', text: error.message });
    } finally { setSaving(false); }
  };

  const Container = modal ? SignatureModalForm : SettingsCard;
  return (
    <Container as="form" onSubmit={save} onClick={(event) => event.stopPropagation()} role={modal ? 'dialog' : undefined} aria-modal={modal ? 'true' : undefined} aria-labelledby={modal ? 'signature-settings-title' : undefined}>
      {modal ? (
        <SignatureModalHeader>
          <div><CardTitle id="signature-settings-title">서명 설정</CardTitle><CardDescription>작성 화면을 벗어나지 않고 자동 서명을 설정합니다.</CardDescription></div>
          <IconButton type="button" onClick={onClose} aria-label="서명 설정 닫기"><MdClose size={20} /></IconButton>
        </SignatureModalHeader>
      ) : <CardTitle>메일 서명</CardTitle>}
      <CardDescription>서명 사용을 켜면 개인·공용 주소로 메일을 발송할 때 현재 로그인한 계정의 서명이 자동 첨부됩니다.</CardDescription>
      {status && <Notice type={status.type}>{status.text}</Notice>}
      <SignatureFormGrid>
        <SignatureField $wide>
          <label>회사</label>
          <SignatureCompany><img src={ieumLogo} alt="" /><strong>{form.company}</strong></SignatureCompany>
        </SignatureField>
        <SignatureField>
          <label htmlFor="signature-department">부서명</label>
          <Input id="signature-department" name="department" value={form.department} onChange={updateField} maxLength={80} placeholder="예: 운영팀" />
        </SignatureField>
        <SignatureField>
          <label htmlFor="signature-position">직책</label>
          <Input id="signature-position" name="position" value={form.position} onChange={updateField} maxLength={60} readOnly={form.positionLocked} aria-readonly={form.positionLocked} />
          {form.positionLocked && <small>운영관리자 직책은 이사로 고정됩니다.</small>}
        </SignatureField>
        <SignatureField>
          <label htmlFor="signature-name">이름</label>
          <Input id="signature-name" name="name" value={form.name} onChange={updateField} maxLength={80} placeholder="서명에 표시할 이름" />
        </SignatureField>
        <SignatureField>
          <label htmlFor="signature-phone">연락처</label>
          <Input id="signature-phone" name="phone" value={form.phone} onChange={updateField} inputMode="tel" maxLength={13} pattern="010-[0-9]{4}-[0-9]{4}" placeholder="010-0000-0000" title="010으로 시작하는 11자리 전화번호" />
        </SignatureField>
        <SignatureField>
          <label htmlFor="signature-email">이메일</label>
          <Input id="signature-email" value={form.email} readOnly aria-readonly="true" />
          <small>현재 개인 메일 주소가 자동 표시됩니다.</small>
        </SignatureField>
        <SignatureField $wide>
          <label htmlFor="signature-address">주소</label>
          <Input id="signature-address" value={form.address} readOnly aria-readonly="true" />
        </SignatureField>
      </SignatureFormGrid>
      <SignatureToggle>
        <input id="signature-enabled" type="checkbox" name="enabled" checked={form.enabled} onChange={updateField} />
        <label htmlFor="signature-enabled"><strong>서명 사용</strong><span>체크하면 발송 시 자동으로 첨부됩니다.</span></label>
      </SignatureToggle>
      <SignaturePreviewBlock>
        <span>미리보기</span>
        <MailSignaturePreview signature={form} />
      </SignaturePreviewBlock>
      <SignatureActions>
        {modal && <SecondaryButton type="button" onClick={onClose} disabled={saving}>취소</SecondaryButton>}
        <PrimaryButton type="submit" disabled={saving}>{saving ? '저장 중...' : '서명 설정 저장'}</PrimaryButton>
      </SignatureActions>
    </Container>
  );
};

const AdminMailboxManager = () => {
  const navigate = useNavigate();
  const [mailboxes, setMailboxes] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState('');
  const [editFields, setEditFields] = useState({ mailId: '', description: '', ownerProfileId: '' });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const result = await listManagedMailboxes();
      setMailboxes(result.mailboxes || []);
      setAssignableUsers(result.assignableUsers || []);
    } catch (requestError) { setError(requestError.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const beginEdit = (mailbox) => {
    setEditingId(mailbox.id);
    const matchedOwner = mailbox.owner?.profileId || assignableUsers.find((user) => user.email && user.email === mailbox.owner?.ownerEmail)?.id || '';
    setEditFields({ mailId: mailbox.address.split('@')[0], description: mailbox.description || '', ownerProfileId: matchedOwner });
  };
  const saveEdit = async (id) => {
    setError('');
    try {
      await updateManagedMailbox(id, editFields);
      setEditingId('');
      await load();
    } catch (requestError) { setError(requestError.message); }
  };
  const remove = async () => {
    if (!deleteTarget) return;
    setError('');
    setDeleting(true);
    try { await deleteManagedMailbox(deleteTarget.id); setDeleteTarget(null); await load(); }
    catch (requestError) { setError(requestError.message); }
    finally { setDeleting(false); }
  };

  return (
    <>
    <SettingsCard>
      <SettingsHeader><div><CardTitle>전체 개인 메일 관리</CardTitle><CardDescription>관리자는 모든 개인 메일을 열람·수정·삭제할 수 있으며 작업은 감사 로그에 기록됩니다.</CardDescription></div><IconButton type="button" onClick={load}><MdRefresh size={19} /></IconButton></SettingsHeader>
      {error && <Notice type="error">{error}</Notice>}
      {loading ? <ListState>메일 계정을 불러오는 중...</ListState> : (
        <MessageTableWrap>
          <MessageTable>
            <thead><tr><th>메일 주소</th><th>연결 사용자</th><th>설명</th><th>사용량</th><th>관리</th></tr></thead>
            <tbody>{mailboxes.map((mailbox) => (
              <tr key={mailbox.id}>
                <td data-label="메일 주소">{editingId === mailbox.id ? <CompactInput value={editFields.mailId} onChange={(event) => setEditFields((previous) => ({ ...previous, mailId: event.target.value.toLowerCase() }))} /> : mailbox.address}</td>
                <td data-label="연결 사용자">{editingId === mailbox.id ? <CompactSelect value={editFields.ownerProfileId} onChange={(event) => setEditFields((previous) => ({ ...previous, ownerProfileId: event.target.value }))}>
                  <option value="">연결하지 않음</option>
                  {assignableUsers.map((user) => <option key={user.id} value={user.id}>{user.name || user.email} · {user.role === 'admin' ? '관리자' : '매니저'}{user.email ? ` (${user.email})` : ''}</option>)}
                  </CompactSelect> : (mailbox.owner?.ownerName || mailbox.owner?.ownerEmail || '연결 정보 없음')}</td>
                <td data-label="설명">{editingId === mailbox.id ? <CompactInput value={editFields.description} onChange={(event) => setEditFields((previous) => ({ ...previous, description: event.target.value }))} /> : mailbox.description || '-'}</td>
                <td data-label="사용량">{formatBytes(mailbox.usedDiskQuota)}</td>
                <td data-label="관리"><RowActions>
                  {editingId === mailbox.id ? <><SmallPrimary type="button" onClick={() => saveEdit(mailbox.id)}>저장</SmallPrimary><SmallButton type="button" onClick={() => setEditingId('')}>취소</SmallButton></> : <>
                    <SmallButton type="button" disabled={!mailbox.readable} title={mailbox.readable ? '받은편지함 열람' : '연결 정보가 없어 열람할 수 없습니다.'} onClick={() => navigate(`/admin/mail/inbox?as=${encodeURIComponent(mailbox.address)}`)}><MdVisibility /> 열람</SmallButton>
                    <SmallButton type="button" disabled={mailbox.protected} onClick={() => beginEdit(mailbox)}><MdEdit /> 수정</SmallButton>
                    <SmallDanger type="button" disabled={mailbox.protected} onClick={() => setDeleteTarget(mailbox)}><MdDeleteOutline /> 삭제</SmallDanger>
                  </>}
                </RowActions></td>
              </tr>
            ))}</tbody>
          </MessageTable>
        </MessageTableWrap>
      )}
    </SettingsCard>
    {deleteTarget && <ModalOverlay role="presentation" onClick={() => !deleting && setDeleteTarget(null)}>
      <ConfirmModal role="dialog" aria-modal="true" aria-labelledby="mail-delete-title" onClick={(event) => event.stopPropagation()}>
        <ModalIcon><MdDeleteOutline size={26} /></ModalIcon>
        <h3 id="mail-delete-title">메일 계정을 삭제하시겠습니까?</h3>
        <p><strong>{deleteTarget.address}</strong> 계정과 저장된 모든 메일이 함께 삭제되며 복구할 수 없습니다.</p>
        {deleteTarget.owner && <p>현재 연결 사용자: {deleteTarget.owner.ownerName || deleteTarget.owner.ownerEmail}</p>}
        <ModalButtons>
          <SecondaryButton type="button" disabled={deleting} onClick={() => setDeleteTarget(null)}>취소</SecondaryButton>
          <ConfirmDeleteButton type="button" disabled={deleting} onClick={remove}>{deleting ? '삭제 중...' : '확인 후 삭제'}</ConfirmDeleteButton>
        </ModalButtons>
      </ConfirmModal>
    </ModalOverlay>}
    </>
  );
};

const SpamSenderSettings = ({ session, reloadSession }) => {
  const [error, setError] = useState('');
  const remove = async (sender, address = '') => {
    setError('');
    try { await removeSpamSender(sender, address); await reloadSession(); }
    catch (requestError) { setError(requestError.message); }
  };
  if (!session.configured) return null;
  return (
    <SettingsCard>
      <CardTitle>내 스팸 차단 주소</CardTitle>
      <CardDescription>스팸등록한 발신자의 새 메일은 스팸메일함으로 자동 분류됩니다.</CardDescription>
      {error && <Notice type="error">{error}</Notice>}
      {(session.spamSenders?.length || Object.values(session.sharedSpamSenders || {}).some((items) => items.length)) ? <SpamGroups>
        {session.spamSenders?.length > 0 && <div><strong>개인 메일</strong><BlockedList>{session.spamSenders.map((sender) => <span key={sender}>{sender}<button type="button" onClick={() => remove(sender)}>차단 해제</button></span>)}</BlockedList></div>}
        {Object.entries(session.sharedSpamSenders || {}).map(([address, senders]) => senders.length > 0 && <div key={address}><strong>{localPartLabel(address)} 공용메일</strong><BlockedList>{senders.map((sender) => <span key={sender}>{sender}<button type="button" onClick={() => remove(sender, address)}>차단 해제</button></span>)}</BlockedList></div>)}
      </SpamGroups> : <Muted>등록된 스팸 발신자가 없습니다.</Muted>}
    </SettingsCard>
  );
};

const SettingsView = ({ session, reloadSession, role }) => (
  <SettingsStack>
    <MailIdCard session={session} onCreated={reloadSession} />
    <SignatureSettings session={session} reloadSession={reloadSession} />
    <SpamSenderSettings session={session} reloadSession={reloadSession} />
    {role === 'admin' ? <AdminMailboxManager /> : (
      <Notice>매니저 계정에서는 본인의 개인 메일 ID 생성과 연결 정보만 확인할 수 있습니다.</Notice>
    )}
  </SettingsStack>
);

const AdminMailPage = () => {
  const { section = 'inbox' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { role } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const meta = SECTION_META[section] || SECTION_META.inbox;
  const Icon = meta.icon;

  const loadSession = useCallback(async () => {
    setLoading(true); setError('');
    try { setSession(await getMailSession()); }
    catch (requestError) { setError(requestError.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    if (!SECTION_META[section]) navigate('/admin/mail/inbox', { replace: true });
  }, [section, navigate]);
  useEffect(() => { void loadSession(); }, [loadSession]);

  return (
    <Page>
      <Header>
        <HeaderIcon><Icon size={22} /></HeaderIcon>
        <div><Title>{meta.title}</Title><Description>{meta.description}</Description></div>
      </Header>
      {error && <Notice type="error">{error}</Notice>}
      {loading && <ContentState>메일 서버에 연결하는 중...</ContentState>}
      {!loading && session && section !== 'settings' && !session.configured && <SetupRequired />}
      {!loading && session?.configured && section === 'compose' && <ComposeView session={session} seed={location.state?.mailDraft} reloadSession={loadSession} />}
      {!loading && session?.configured && ['inbox', 'shared', 'important', 'sent', 'drafts', 'archive', 'spam', 'trash'].includes(section) && <MailboxView session={session} section={section} />}
      {!loading && session && section === 'settings' && <SettingsView session={session} reloadSession={loadSession} role={role} />}
    </Page>
  );
};

export default AdminMailPage;

const Page = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
`;
const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 14px;
    padding: 0 2px;
  }
`;
const HeaderIcon = styled.div`
  display: flex;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #EEF2FF;
  color: ${({ theme }) => theme.colors.primary};

  @media (max-width: 768px) { width: 38px; height: 38px; flex-basis: 38px; }
`;
const Title = styled.h1`margin:0 0 4px;font-size:20px;color:${({ theme }) => theme.colors.text};`;
const Description = styled.p`margin:0;font-size:13px;color:${({ theme }) => theme.colors.textSecondary};`;
const ContentState = styled.div`padding:72px 20px;text-align:center;color:${({ theme }) => theme.colors.textSecondary};background:#fff;border:1px solid ${({ theme }) => theme.colors.border};border-radius:12px;`;
const NoticeBox = styled.div`
  margin:0 0 14px;padding:11px 13px;border-radius:8px;font-size:13px;line-height:1.5;
  color:${({ $type }) => $type === 'error' ? '#B91C1C' : $type === 'success' ? '#15803D' : '#3451D1'};
  background:${({ $type }) => $type === 'error' ? '#FEF2F2' : $type === 'success' ? '#F0FDF4' : '#EEF2FF'};
  border:1px solid ${({ $type }) => $type === 'error' ? '#FECACA' : $type === 'success' ? '#BBF7D0' : '#DCE4FF'};
`;
const EmptyCard = styled.div`padding:70px 24px;text-align:center;background:#fff;border:1px solid ${({ theme }) => theme.colors.border};border-radius:12px;color:${({ theme }) => theme.colors.textSecondary};svg{color:${({ theme }) => theme.colors.primary};}h3{margin:14px 0 7px;color:${({ theme }) => theme.colors.text};font-size:17px;}p{margin:0 0 20px;font-size:13px;}`;
const ComposeCard = styled.div`
  padding: 22px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  background: #fff;
  box-shadow: ${({ theme }) => theme.shadow.sm};

  @media (max-width: 768px) { padding: 14px; }
`;
const FormRow = styled.div`display:grid;grid-template-columns:100px 1fr;align-items:center;border-bottom:1px solid ${({ theme }) => theme.colors.borderLight};@media(max-width:768px){grid-template-columns:1fr;gap:6px;padding:9px 0;}`;
const FieldLabel = styled.label`font-size:13px;font-weight:600;color:${({ theme }) => theme.colors.textSecondary};`;
const Input = styled.input`width:100%;min-width:0;padding:11px 12px;border:1px solid ${({ theme }) => theme.colors.border};border-radius:8px;background:#fff;color:${({ theme }) => theme.colors.text};font-size:14px;&:focus{outline:none;border-color:${({ theme }) => theme.colors.primary};box-shadow:0 0 0 3px rgba(74,108,247,.1);}`;
const RecipientInputWrap = styled.div`position:relative;min-width:0;padding:6px 0;`;
const RecipientInputBox = styled.div`display:flex;min-height:44px;align-items:center;gap:6px;flex-wrap:wrap;padding:5px 6px;border:1px solid ${({ theme, $error }) => $error ? '#DC2626' : theme.colors.border};border-radius:8px;background:#fff;&:focus-within{border-color:${({ theme }) => theme.colors.primary};box-shadow:0 0 0 3px rgba(74,108,247,.1);}input{flex:1;min-width:180px;border:0;outline:0;padding:6px 5px;font-size:13px;color:${({ theme }) => theme.colors.text};}@media(max-width:768px){input{min-width:130px;}}`;
const RecipientTag = styled.span`display:inline-flex;max-width:100%;align-items:center;gap:5px;padding:5px 5px 5px 8px;border:1px solid #C7D2FE;border-radius:7px;background:#EEF2FF;color:${({ theme }) => theme.colors.primary};font-size:12px;font-weight:600;span{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}button{display:flex;width:22px;height:22px;align-items:center;justify-content:center;border-radius:5px;background:#fff;color:${({ theme }) => theme.colors.primary};}`;
const AddressBookButton = styled.button`display:inline-flex;min-height:32px;align-items:center;justify-content:center;gap:4px;padding:6px 9px;border:1px solid ${({ theme }) => theme.colors.border};border-radius:6px;background:#fff;color:${({ theme }) => theme.colors.textSecondary};font-size:11px;font-weight:600;white-space:nowrap;`;
const RecipientPositionSelect = styled.select`min-height:32px;max-width:150px;padding:6px 24px 6px 8px;border:1px solid ${({ theme }) => theme.colors.border};border-radius:6px;background:#fff;color:${({ theme }) => theme.colors.textSecondary};font-size:11px;font-weight:600;&:disabled{opacity:.55;}@media(max-width:768px){max-width:125px;}`;
const RecipientInputError = styled.span`display:block;margin-top:5px;font-size:11px;color:#DC2626;`;
const ContactSuggestions = styled.div`position:absolute;z-index:30;top:calc(100% - 4px);left:0;right:0;overflow:hidden;border:1px solid ${({ theme }) => theme.colors.border};border-radius:9px;background:#fff;box-shadow:${({ theme }) => theme.shadow.md};button{display:grid;width:100%;grid-template-columns:minmax(100px,180px) minmax(0,1fr);gap:10px;padding:10px 12px;text-align:left;border-bottom:1px solid ${({ theme }) => theme.colors.borderLight};}button:last-child{border-bottom:0;}button:hover{background:#F8FAFC;}strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:12px;color:${({ theme }) => theme.colors.text};}span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:12px;color:${({ theme }) => theme.colors.textSecondary};}@media(max-width:768px){button{grid-template-columns:1fr;}span{font-size:11px;}}`;
const CompactInput = styled(Input)`min-width:120px;padding:7px 8px;font-size:12px;@media(max-width:768px){min-width:0;}`;
const Select = styled.select`min-width:220px;padding:10px 34px 10px 11px;border:1px solid ${({ theme }) => theme.colors.border};border-radius:8px;background:#fff;color:${({ theme }) => theme.colors.text};font-size:13px;@media(max-width:768px){width:100%;}`;
const CompactSelect = styled(Select)`min-width:220px;max-width:360px;padding:7px 28px 7px 8px;font-size:12px;@media(max-width:768px){min-width:0;max-width:none;}`;
const FormRowInput = `width:100%;border:0;padding:13px 0;font-size:14px;outline:none;background:transparent;`;
const MessageInput = styled.textarea`${FormRowInput}min-height:320px;resize:vertical;line-height:1.7;padding:20px 4px;@media(max-width:768px){min-height:240px;padding:16px 4px;}`;
const ComposeSignatureArea = styled.div`margin-top:10px;padding:12px 14px;border:1px dashed ${({ theme }) => theme.colors.border};border-radius:10px;background:#FAFBFF;`;
const ComposeSignatureHeader = styled.div`display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:8px;>span{font-size:11px;font-weight:600;color:${({ theme }) => theme.colors.textSecondary};}@media(max-width:768px){align-items:stretch;>button{min-height:40px;}}`;
const ComposeFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 14px;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 8px;

    > span { grid-column: 1 / -1; }
    > button { width: 100%; min-height: 44px; }
  }
`;
const FileLabel = styled.label`display:inline-flex;min-height:40px;align-items:center;justify-content:center;gap:5px;padding:9px 12px;border:1px solid ${({ theme }) => theme.colors.border};border-radius:8px;font-size:13px;cursor:pointer;input{display:none;}@media(max-width:768px){min-height:44px;}`;
const FileSummary = styled.span`font-size:12px;color:${({ theme }) => theme.colors.textSecondary};margin-right:auto;`;
const PrimaryButton = styled.button`display:inline-flex;min-height:40px;align-items:center;justify-content:center;gap:6px;padding:10px 16px;border-radius:8px;background:${({ theme }) => theme.colors.primary};color:#fff;font-size:13px;font-weight:600;&:disabled{opacity:.55;cursor:not-allowed;}@media(max-width:768px){min-height:44px;}`;
const SecondaryButton = styled.button`display:inline-flex;min-height:38px;align-items:center;justify-content:center;gap:5px;padding:8px 11px;border:1px solid ${({ theme }) => theme.colors.border};border-radius:8px;background:#fff;color:${({ theme }) => theme.colors.textSecondary};font-size:12px;@media(max-width:768px){min-height:42px;}`;
const DangerButton = styled(SecondaryButton)`border-color:#FECACA;color:#DC2626;`;
const IconButton = styled(SecondaryButton)`padding:8px;`;
const MailboxCard = styled.section`background:#fff;border:1px solid ${({ theme }) => theme.colors.border};border-radius:12px;overflow:hidden;box-shadow:${({ theme }) => theme.shadow.sm};${NoticeBox}{margin:12px;}`;
const MailboxToolbar = styled.div`display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px;border-bottom:1px solid ${({ theme }) => theme.colors.border};>div{display:flex;align-items:center;gap:9px;}@media(max-width:768px){padding:12px;align-items:stretch;flex-direction:column;}`;
const ToolbarGroup = styled.div`display:flex;align-items:center;gap:9px;flex-wrap:wrap;@media(max-width:768px){display:grid!important;grid-template-columns:minmax(0,1fr) minmax(0,1fr);align-items:stretch!important;>select{min-width:0;}span{justify-self:start;}}`;
const ToolbarActions = styled.div`display:flex!important;flex-direction:row!important;justify-content:flex-end;@media(max-width:768px){display:grid!important;grid-template-columns:minmax(0,1fr) minmax(0,1fr) 44px;>button{width:100%;padding-left:7px;padding-right:7px;}}`;
const CountBadge = styled.span`padding:6px 9px;border-radius:999px;background:#F1F5F9;color:#475569;font-size:12px;font-weight:600;white-space:nowrap;`;
const AdminReadBadge = styled.span`display:inline-flex;padding:6px 9px;border-radius:999px;background:#FFF7ED;color:#C2410C;font-size:12px;font-weight:600;`;
const FilterToolbar = styled.div`display:flex;align-items:center;gap:10px;padding:10px 14px;border-bottom:1px solid ${({ theme }) => theme.colors.borderLight};@media(max-width:768px){padding:10px 12px;display:grid;grid-template-columns:minmax(0,1fr) 120px;align-items:stretch;}`;
const SearchBox = styled.label`flex:1;display:flex;align-items:center;gap:7px;padding:8px 10px;border:1px solid ${({ theme }) => theme.colors.border};border-radius:8px;color:${({ theme }) => theme.colors.textSecondary};input{flex:1;min-width:0;border:0;outline:0;font-size:12px;}`;
const FilterSelect = styled(Select)`min-width:140px;padding-top:8px;padding-bottom:8px;`;
const BulkToolbar = styled.div`display:flex;align-items:center;gap:7px;flex-wrap:wrap;padding:10px 14px;background:#F8FAFC;border-bottom:1px solid ${({ theme }) => theme.colors.border};font-size:12px;strong{margin-right:6px;}@media(max-width:768px){padding:10px 12px;strong{width:100%;}}`;
const ListState = styled.div`padding:72px 20px;text-align:center;color:${({ theme }) => theme.colors.textSecondary};font-size:13px;`;
const MessageTableWrap = styled.div`overflow-x:auto;@media(max-width:768px){overflow:visible;}`;
const MessageTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th { padding:11px 13px;text-align:left;background:#F8FAFC;color:${({ theme }) => theme.colors.textSecondary};font-size:12px;border-bottom:1px solid ${({ theme }) => theme.colors.border};white-space:nowrap; }
  td { padding:12px 13px;border-bottom:1px solid ${({ theme }) => theme.colors.borderLight};color:${({ theme }) => theme.colors.text};vertical-align:middle; }
  tr:last-child td { border-bottom: 0; }

  @media (max-width: 768px) {
    display: block;
    thead { display: none; }
    tbody { display: flex; flex-direction: column; gap: 10px; padding: 10px; }
    tbody > tr:not([data-mail-row]) {
      display: block;
      padding: 12px;
      border: 1px solid ${({ theme }) => theme.colors.border};
      border-radius: 10px;
      background: #fff;
    }
    tbody > tr:not([data-mail-row]) > td {
      display: grid;
      grid-template-columns: 86px minmax(0, 1fr);
      gap: 10px;
      align-items: start;
      padding: 8px 0;
      border: 0;
      overflow-wrap: anywhere;
    }
    tbody > tr:not([data-mail-row]) > td::before {
      content: attr(data-label);
      color: ${({ theme }) => theme.colors.textSecondary};
      font-size: 11px;
      font-weight: 600;
    }
  }
`;
const MessageRow = styled.tr`
  cursor: pointer;
  background: ${({ $unread }) => $unread ? '#F5F8FF' : '#fff'};
  font-weight: ${({ $unread }) => $unread ? 600 : 400};
  &:hover { background: #F8FAFC; }
  td { white-space: nowrap; }
  td:nth-last-child(3) { min-width: 260px; white-space: normal; }
  td:nth-last-child(2), td:last-child { color: ${({ theme }) => theme.colors.textSecondary}; }

  @media (max-width: 768px) {
    && {
      display: grid;
      grid-template-columns: 30px 30px minmax(0, 1fr);
      grid-template-areas:
        "select star recipient"
        "select star sender"
        "select star subject"
        "select star date";
      column-gap: 6px;
      row-gap: 4px;
      padding: 13px 10px;
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }
    &&:last-child { border-bottom: 0; }
    && > td {
      min-width: 0;
      padding: 0;
      border: 0;
      white-space: normal;
      overflow-wrap: anywhere;
    }
    [data-cell="select"] { grid-area: select; padding-top: 2px; }
    [data-cell="star"] { grid-area: star; }
    [data-cell="recipient"] { grid-area: recipient; }
    [data-cell="sender"] { grid-area: sender; overflow:hidden;text-overflow:ellipsis;white-space:nowrap; }
    [data-cell="subject"] { grid-area: subject; line-height: 1.45; }
    [data-cell="date"] { grid-area: date; color:${({ theme }) => theme.colors.textSecondary};font-size:11px;font-weight:400; }
    [data-cell="date"]::after { content: " · " attr(data-size); }
    [data-cell="size"] { display: none; }
  }
`;
const StarButton = styled.button`display:flex;align-items:center;justify-content:center;color:#F59E0B;font-size:18px;`;
const RecipientBadge = styled.span`display:inline-flex;padding:3px 7px;border-radius:999px;background:#EEF2FF;color:${({ theme }) => theme.colors.primary};font-size:11px;font-weight:600;`;
const MessageDetail = styled.section`background:#fff;border:1px solid ${({ theme }) => theme.colors.border};border-radius:12px;padding:22px;box-shadow:${({ theme }) => theme.shadow.sm};@media(max-width:768px){padding:14px;}`;
const DetailToolbar = styled.div`display:flex;align-items:center;gap:10px;margin-bottom:22px;@media(max-width:768px){align-items:stretch;flex-direction:column;margin-bottom:18px;}`;
const DetailActions = styled.div`display:flex;align-items:center;gap:7px;flex-wrap:wrap;`;
const Muted = styled.span`font-size:12px;color:${({ theme }) => theme.colors.textSecondary};`;
const DetailSubject = styled.h2`margin:0 0 18px;font-size:19px;overflow-wrap:anywhere;color:${({ theme }) => theme.colors.text};@media(max-width:768px){font-size:17px;line-height:1.45;}`;
const DetailMeta = styled.div`display:grid;grid-template-columns:85px minmax(0,1fr);gap:8px;margin:6px 0;font-size:12px;color:${({ theme }) => theme.colors.textSecondary};strong{color:${({ theme }) => theme.colors.text};overflow-wrap:anywhere;}@media(max-width:768px){grid-template-columns:62px minmax(0,1fr);}`;
const ContactMetaValue = styled.div`display:flex;min-width:0;align-items:center;gap:8px;flex-wrap:wrap;>span{overflow-wrap:anywhere;}`;
const InlineContactNotice = styled.div`margin:10px 0;padding:8px 10px;border-radius:7px;background:${({ $error }) => $error ? '#FEF2F2' : '#F0FDF4'};color:${({ $error }) => $error ? '#B91C1C' : '#15803D'};font-size:12px;`;
const AttachmentList = styled.div`display:flex;flex-wrap:wrap;gap:8px;margin:16px 0;padding:12px;border-radius:8px;background:#F8FAFC;span{display:flex;align-items:center;gap:4px;font-size:12px;}`;
const MessageBody = styled.pre`margin:24px 0 0;padding-top:22px;border-top:1px solid ${({ theme }) => theme.colors.border};white-space:pre-wrap;overflow-wrap:anywhere;font-family:inherit;font-size:14px;line-height:1.75;color:${({ theme }) => theme.colors.text};`;
const SettingsStack = styled.div`display:flex;flex-direction:column;gap:18px;`;
const SettingsCard = styled.section`padding:20px;background:#fff;border:1px solid ${({ theme }) => theme.colors.border};border-radius:12px;box-shadow:${({ theme }) => theme.shadow.sm};@media(max-width:768px){padding:14px;}`;
const SettingsHeader = styled.div`display:flex;justify-content:space-between;align-items:flex-start;gap:12px;`;
const CardTitle = styled.h2`margin:0 0 7px;font-size:16px;color:${({ theme }) => theme.colors.text};`;
const CardDescription = styled.p`margin:0 0 16px;font-size:12px;line-height:1.6;color:${({ theme }) => theme.colors.textSecondary};`;
const ConfiguredBox = styled.div`display:flex;align-items:center;gap:11px;padding:15px;border-radius:9px;background:#F7F9FF;color:${({ theme }) => theme.colors.primary};div{display:flex;min-width:0;flex-direction:column;gap:3px;}strong{font-size:15px;overflow-wrap:anywhere;}span{font-size:12px;color:${({ theme }) => theme.colors.textSecondary};}`;
const MailIdLine = styled.div`display:grid;grid-template-columns:minmax(180px,360px) auto auto;align-items:center;gap:10px;@media(max-width:768px){grid-template-columns:minmax(0,1fr) auto;>button{grid-column:1/-1;width:100%;}}`;
const DomainSuffix = styled.span`font-size:14px;font-weight:600;color:${({ theme }) => theme.colors.text};`;
const SignatureFormGrid = styled.div`display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:14px 16px;@media(max-width:768px){grid-template-columns:1fr;}`;
const SignatureField = styled.div`display:flex;min-width:0;flex-direction:column;gap:6px;${({ $wide }) => $wide && 'grid-column:1/-1;'}>label{font-size:12px;font-weight:600;color:${({ theme }) => theme.colors.textSecondary};}>small{font-size:11px;line-height:1.5;color:${({ theme }) => theme.colors.textSecondary};}input[readonly]{background:#F8FAFC;color:#64748B;}`;
const SignatureCompany = styled.div`display:flex;min-height:48px;align-items:center;gap:12px;padding:10px 12px;border:1px solid ${({ theme }) => theme.colors.border};border-radius:8px;background:#F8FAFC;img{width:72px;max-height:32px;object-fit:contain;}strong{font-size:15px;}`;
const SignatureToggle = styled.div`display:flex;align-items:flex-start;gap:10px;margin-top:18px;padding:13px 14px;border-radius:9px;background:#F7F9FF;input{width:18px;height:18px;margin-top:2px;accent-color:${({ theme }) => theme.colors.primary};}label{display:flex;flex-direction:column;gap:3px;cursor:pointer;}strong{font-size:13px;color:${({ theme }) => theme.colors.text};}span{font-size:11px;color:${({ theme }) => theme.colors.textSecondary};}`;
const SignaturePreviewBlock = styled.div`margin-top:16px;>span{display:block;margin-bottom:7px;font-size:11px;font-weight:600;color:${({ theme }) => theme.colors.textSecondary};}`;
const SignaturePreview = styled.div`display:flex;align-items:flex-start;gap:${({ $compact }) => $compact ? '12px' : '18px'};padding:${({ $compact }) => $compact ? '12px' : '18px'};border:1px solid ${({ theme }) => theme.colors.border};border-radius:10px;background:#fff;@media(max-width:480px){gap:10px;padding:12px;}`;
const SignatureLogo = styled.img`width:96px;max-height:54px;object-fit:contain;flex:0 0 auto;@media(max-width:480px){width:72px;}`;
const SignatureDetails = styled.div`display:flex;min-width:0;flex-direction:column;gap:2px;font-size:12px;line-height:1.55;color:#4B5563;overflow-wrap:anywhere;>strong{font-size:15px;color:#111827;}>b{color:#1F2937;}>span:last-child{color:#6B7280;}>span i{font-style:normal;color:#D1D5DB;}`;
const SignatureActions = styled.div`display:flex;justify-content:flex-end;margin-top:14px;@media(max-width:768px){>button{width:100%;}}`;
const SignatureModalForm = styled.div`width:100%;max-width:820px;max-height:calc(100vh - 40px);overflow-y:auto;padding:22px;border-radius:14px;background:#fff;box-shadow:${({ theme }) => theme.shadow.lg};@media(max-width:768px){max-height:calc(100dvh - 24px);padding:18px 14px;}`;
const SignatureModalHeader = styled.div`display:flex;align-items:flex-start;justify-content:space-between;gap:16px;margin-bottom:14px;${CardDescription}{margin-top:4px;}`;
const AddressBookModal = styled.div`width:100%;max-width:650px;max-height:calc(100vh - 40px);overflow:hidden;padding:22px;border-radius:14px;background:#fff;box-shadow:${({ theme }) => theme.shadow.lg};@media(max-width:768px){max-height:calc(100dvh - 24px);padding:18px 14px;}`;
const AddressBookSearch = styled.label`display:flex;align-items:center;gap:7px;padding:9px 11px;border:1px solid ${({ theme }) => theme.colors.border};border-radius:8px;color:${({ theme }) => theme.colors.textSecondary};input{flex:1;min-width:0;border:0;outline:0;font-size:13px;}`;
const AddressBookSelectAll = styled.label`display:flex;align-items:center;gap:8px;padding:10px 3px;font-size:12px;color:${({ theme }) => theme.colors.textSecondary};input{width:17px;height:17px;accent-color:${({ theme }) => theme.colors.primary};}b{margin-left:auto;color:${({ theme }) => theme.colors.text};}`;
const AddressBookList = styled.div`max-height:360px;overflow-y:auto;border:1px solid ${({ theme }) => theme.colors.border};border-radius:9px;`;
const AddressBookRow = styled.div`display:flex;align-items:center;gap:10px;padding:10px 11px;border-bottom:1px solid ${({ theme }) => theme.colors.borderLight};&:last-child{border-bottom:0;}label{display:flex;min-width:0;flex:1;align-items:center;gap:9px;cursor:pointer;}input{width:17px;height:17px;accent-color:${({ theme }) => theme.colors.primary};}label span{display:flex;min-width:0;flex-direction:column;gap:2px;}strong,small{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}strong{font-size:12px;color:${({ theme }) => theme.colors.text};}small{font-size:11px;color:${({ theme }) => theme.colors.textSecondary};}`;
const InternalContactMark = styled.span`flex:none;padding:4px 7px;border-radius:999px;background:#EEF2FF;color:#4A6CF7;font-size:10px;font-weight:700;`;
const AddressBookEmpty = styled.div`padding:52px 18px;text-align:center;color:${({ theme }) => theme.colors.textSecondary};font-size:12px;`;
const RowActions = styled.div`display:flex;align-items:center;gap:5px;white-space:nowrap;@media(max-width:768px){flex-wrap:wrap;white-space:normal;}`;
const SmallButton = styled.button`display:inline-flex;min-height:32px;align-items:center;justify-content:center;gap:3px;padding:6px 8px;border:1px solid ${({ theme }) => theme.colors.border};border-radius:6px;background:#fff;color:${({ theme }) => theme.colors.textSecondary};font-size:11px;&:disabled{opacity:.4;cursor:not-allowed;}@media(max-width:768px){min-height:38px;}`;
const SmallPrimary = styled(SmallButton)`border-color:#C7D2FE;color:${({ theme }) => theme.colors.primary};background:#EEF2FF;`;
const SmallDanger = styled(SmallButton)`border-color:#FECACA;color:#DC2626;background:#FFF;`;
const BlockedList = styled.div`display:flex;flex-wrap:wrap;gap:8px;span{display:inline-flex;align-items:center;gap:8px;padding:7px 9px;border-radius:8px;background:#FEF2F2;color:#991B1B;font-size:12px;}button{font-size:11px;color:#DC2626;text-decoration:underline;}`;
const SpamGroups = styled.div`display:flex;flex-direction:column;gap:13px;>div>strong{display:block;margin-bottom:7px;font-size:12px;color:${({ theme }) => theme.colors.textSecondary};}`;
const ModalOverlay = styled.div`position:fixed;inset:0;z-index:1200;display:flex;align-items:center;justify-content:center;padding:20px;background:rgba(15,23,42,.45);@media(max-width:768px){align-items:flex-end;padding:12px 12px calc(12px + env(safe-area-inset-bottom));}`;
const ConfirmModal = styled.div`width:100%;max-width:440px;padding:24px;border-radius:14px;background:#fff;box-shadow:${({ theme }) => theme.shadow.lg};text-align:center;h3{margin:12px 0 8px;font-size:18px;color:${({ theme }) => theme.colors.text};}p{margin:6px 0;font-size:13px;line-height:1.6;color:${({ theme }) => theme.colors.textSecondary};}@media(max-width:768px){padding:20px 16px;}`;
const ModalIcon = styled.div`width:48px;height:48px;margin:0 auto;display:flex;align-items:center;justify-content:center;border-radius:50%;background:#FEF2F2;color:#DC2626;`;
const ModalButtons = styled.div`display:flex;justify-content:center;gap:9px;margin-top:22px;`;
const ConfirmDeleteButton = styled(PrimaryButton)`background:#DC2626;`;
